import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  LockOutlined,
  CrownOutlined,
  MailOutlined,
  InfoCircleOutlined,
  BarChartOutlined,
  ShoppingCartOutlined,
  GlobalOutlined,
  DownloadOutlined,
  FileTextOutlined,
  HistoryOutlined,
  TableOutlined,
  UploadOutlined,
  ShareAltOutlined,
  PictureOutlined,
  FileOutlined,
} from "@ant-design/icons";
import RequestAccessModal from "./RequestAccessModal";

// Vendor Feature Lock (ADO #1597) — per-feature card copy, matches
// VendorFeatureLock_Locked_Mockup.html (title/blurb/benefits per section).
const FEATURE_CONTENT = {
  reports: {
    title: "Unlock Reports",
    blurb:
      "Explore how your jewelry and diamonds perform — chart reports, add-to-cart and wishlist insights, and global benchmarks.",
    benefits: [
      { icon: <BarChartOutlined />, text: "Jewelry & diamond charts" },
      { icon: <ShoppingCartOutlined />, text: "Add-to-cart & wishlist" },
      { icon: <GlobalOutlined />, text: "Global benchmarks" },
      { icon: <DownloadOutlined />, text: "Export to CSV" },
    ],
  },
  po: {
    title: "Unlock Purchase Orders",
    blurb:
      "Create and send purchase orders, track order status and history, and manage vendor worksheets — all in one place.",
    benefits: [
      { icon: <FileTextOutlined />, text: "Create & send POs" },
      { icon: <HistoryOutlined />, text: "Order history" },
      { icon: <TableOutlined />, text: "Vendor worksheets" },
      { icon: <DownloadOutlined />, text: "Export to CSV" },
    ],
  },
  marketing: {
    title: "Unlock Marketing",
    blurb:
      "Upload and manage marketing materials, share brand assets with your retailers, and keep collateral organized in one library.",
    benefits: [
      { icon: <UploadOutlined />, text: "Upload materials" },
      { icon: <ShareAltOutlined />, text: "Share with retailers" },
      { icon: <PictureOutlined />, text: "Asset library" },
      { icon: <FileOutlined />, text: "Vendor uploads" },
    ],
  },
};

// Vendor Feature Lock (ADO #1597) — rendered by FeatureGuard in place of a locked
// route. Inherits Layout from PrivateRoute, same convention as PageNotAccess.
const FeatureLocked = ({ feature }) => {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const content = FEATURE_CONTENT[feature] || {
    title: `Unlock ${feature}`,
    blurb: "",
    benefits: [],
  };

  return (
    <div className="feature-locked-wrap">
      <div className="feature-locked-card">
        <div className="feature-locked-lead">
          <div className="feature-locked-badge">
            <LockOutlined />
          </div>
          <div>
            <span className="feature-locked-pill">
              <CrownOutlined /> {t("Premium feature")}
            </span>
            <h2>{t(content.title)}</h2>
          </div>
        </div>

        <p className="feature-locked-blurb">{t(content.blurb)}</p>

        <div className="feature-locked-grid">
          {content.benefits.map((b, i) => (
            <div className="feature-locked-benefit" key={i}>
              {b.icon}
              <span>{t(b.text)}</span>
            </div>
          ))}
        </div>

        <div className="feature-locked-cta">
          <button
            type="button"
            className="feature-locked-btn-primary"
            onClick={() => setModalVisible(true)}
          >
            <MailOutlined /> {t("Contact support to subscribe")}
          </button>
          <button
            type="button"
            className="feature-locked-btn-ghost"
            onClick={() => setModalVisible(true)}
          >
            {t("See plans")}
          </button>
        </div>

        <div className="feature-locked-reassure">
          <InfoCircleOutlined />
          <span>{t("Already upgraded? Sign out and back in to apply.")}</span>
        </div>
      </div>

      <RequestAccessModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        feature={feature}
      />
    </div>
  );
};

export default FeatureLocked;
